/**
 * Phada Design Tokens
 * ====================
 *
 * Fonte unica para cores, espaçamentos, tipos, sombras e raios usados
 * por todos os componentes da PhadaUI e por todas as paginas que
 * seguem o design system.
 *
 * Quando precisar mudar a cor primaria, raio padrao ou espacamento
 * base do app, mude AQUI. Componentes importam destes tokens.
 *
 * Filosofia: "Silent Hierarchy" — ver docs/mockups/design_philosophy.md
 */

// ─────────────────────────────────────────────
//  PALETA — paleta restrita e justificada
// ─────────────────────────────────────────────

export const COLORS = {
  // Marca
  primary: '#7B176E',
  primaryDark: '#5a1151',
  primaryGradientEnd: '#9b4d96',
  primaryGradient: 'linear-gradient(135deg, #7B176E 0%, #9b4d96 100%)',
  primarySoft: '#f4e8f1',
  primarySofter: '#f8f5f8',

  // Neutros (gray scale)
  white: '#ffffff',
  gray50: '#fafafa',
  gray100: '#f5f5f5',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',

  // Texto
  textPrimary: '#212529',
  textSecondary: '#6c757d',
  textMuted: '#9ca3af',
  textInverse: '#ffffff',

  // Borda / fundo
  border: '#e5e7eb',
  borderStrong: '#d1d5db',
  bg: '#ffffff',
  bgMuted: '#f8f5f8',
  bgInverse: '#1c1820',

  // Cores semânticas (WCAG AA com texto branco)
  success: '#16a34a',
  successSoft: '#dcfce7',
  warning: '#ca8a04',
  warningSoft: '#fef9c3',
  danger: '#dc2626',
  dangerSoft: '#fee2e2',
  info: '#2563eb',
  infoSoft: '#dbeafe',
  amber: '#ea580c',
  amberSoft: '#fff4e5',
};

// ─────────────────────────────────────────────
//  SEVERIDADE — uso em badges e indicadores
//  Cada nivel = fg + bg + cor solida WCAG
// ─────────────────────────────────────────────

export const SEVERITY = {
  critica: { solid: COLORS.danger, soft: COLORS.dangerSoft, label: 'Crítica' },
  alta: { solid: COLORS.amber, soft: COLORS.amberSoft, label: 'Alta' },
  media: { solid: COLORS.warning, soft: COLORS.warningSoft, label: 'Média' },
  baixa: { solid: COLORS.info, soft: COLORS.infoSoft, label: 'Baixa' },
};

export const STATUS = {
  ok: { solid: COLORS.success, label: 'OK' },
  novo: { solid: COLORS.primary, label: 'Novo' },
  pendente: { solid: COLORS.info, label: 'Pendente' },
  arquivado: { solid: COLORS.gray500, label: 'Arquivado' },
  erro: { solid: COLORS.danger, label: 'Erro' },
};

// ─────────────────────────────────────────────
//  SPACING — escala 4-base
// ─────────────────────────────────────────────

export const SPACE = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  // Aliases semânticos
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
};

// ─────────────────────────────────────────────
//  RADII — bordas arredondadas consistentes
// ─────────────────────────────────────────────

export const RADIUS = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  pill: '999px',
};

// ─────────────────────────────────────────────
//  ELEVATION — sombras com propósito
// ─────────────────────────────────────────────

export const SHADOW = {
  none: 'none',
  // Card padrão
  sm: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
  // Elementos com destaque
  md: '0 4px 6px rgba(15, 23, 42, 0.05), 0 2px 4px rgba(15, 23, 42, 0.04)',
  // Modal, popover
  lg: '0 12px 24px rgba(15, 23, 42, 0.10), 0 4px 8px rgba(15, 23, 42, 0.06)',
  // Hero com gradiente
  hero: '0 8px 24px rgba(123, 23, 110, 0.25)',
};

// ─────────────────────────────────────────────
//  TIPOGRAFIA — escala matemática
// ─────────────────────────────────────────────

export const FONT = {
  family: "'Rubik', sans-serif",
  familyMono: "'Geist Mono', 'Roboto Mono', monospace",

  weight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  size: {
    xs: '11px',
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '20px',
    xl: '24px',
    xxl: '28px',
    huge: '36px',
  },

  lineHeight: {
    tight: 1.25,
    snug: 1.4,
    normal: 1.5,
    loose: 1.75,
  },

  // Label conventions
  label: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#6b7280',
  },
};

// ─────────────────────────────────────────────
//  TRANSITION — animações sutis
// ─────────────────────────────────────────────

export const TRANSITION = {
  fast: '0.15s ease',
  base: '0.2s ease',
  slow: '0.4s ease',
};

// ─────────────────────────────────────────────
//  Z-INDEX — escala canônica
// ─────────────────────────────────────────────

export const Z = {
  base: 0,
  raised: 10,
  dropdown: 1000,
  sticky: 1020,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  toast: 1080,
  topmost: 9999, // banners persistentes (impersonation)
};

// ─────────────────────────────────────────────
//  Helpers / composições prontas
// ─────────────────────────────────────────────

/** Hero card com gradiente — usado por PageHeader */
export const HERO_GRADIENT_STYLE = {
  background: COLORS.primaryGradient,
  color: COLORS.white,
  padding: '24px 32px',
  borderRadius: RADIUS.lg,
};

/** Estilo de "label small" usado em forms e meta */
export const LABEL_STYLE = {
  ...FONT.label,
  marginBottom: SPACE[1],
};

/** Card padrão */
export const CARD_STYLE = {
  background: COLORS.bg,
  borderRadius: RADIUS.lg,
  boxShadow: SHADOW.sm,
  border: 'none',
};

const tokens = {
  COLORS,
  SEVERITY,
  STATUS,
  SPACE,
  RADIUS,
  SHADOW,
  FONT,
  TRANSITION,
  Z,
  HERO_GRADIENT_STYLE,
  LABEL_STYLE,
  CARD_STYLE,
};

export default tokens;
